import React, { useCallback, useContext, useMemo, useState } from 'react';
import ListingFieldsRequiredVisibleInfoContext from './ListingFieldsRequiredVisibleInfo.context';
import {
  Listing,
  ListingRequiredVisibleInfo,
  REASON_TO_HIDE,
  RequiredVisibleInfo,
} from 'models/Listing';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';

const prepareListingRequiredVisibleInfo = (values: RequiredVisibleInfo) => {
  const {
    isRequired,
    isVisible,
    reasonToHide,
    customReasonToHide,
    revealAfter,
  } = values || {};

  return {
    isRequired,
    isVisible,
    reasonToHide: isVisible ? null : reasonToHide,
    customReasonToHide: isVisible
      ? null
      : reasonToHide === REASON_TO_HIDE.CUSTOM
      ? customReasonToHide
      : null,
    revealAfter: isVisible ? null : revealAfter,
  };
};

type ListingFieldsRequiredVisibleInfoProps = {
  listing: Partial<Listing>;
  canChangeRequired: boolean;
};

const ListingFieldsRequiredVisibleInfo: React.FC<
  ListingFieldsRequiredVisibleInfoProps
> = (props) => {
  const { children, listing, canChangeRequired } = props;

  const {
    _id: listingId,
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    revenueRequiredVisibleInfo,
    COGSRequiredVisibleInfo,
    lessOverheadsRequiredVisibleInfo,
    depreciationAndAmortisationRequiredVisibleInfo,
    EBITDARequiredVisibleInfo,
    EBITRequiredVisibleInfo,
    accountingPeriodRequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
    successionRequiredVisibleInfo,
    hasCommercialPropertyRequiredVisibleInfo,
    revenueModelRequiredVisibleInfo,
  } = listing || {};

  const initialValue = useMemo(
    () => ({
      amountRequiredVisibleInfo,
      amountFromRequiredVisibleInfo,
      amountToRequiredVisibleInfo,
      revenueRequiredVisibleInfo,
      COGSRequiredVisibleInfo,
      lessOverheadsRequiredVisibleInfo,
      depreciationAndAmortisationRequiredVisibleInfo,
      EBITDARequiredVisibleInfo,
      EBITRequiredVisibleInfo,
      accountingPeriodRequiredVisibleInfo,
      averageStockValueRequiredVisibleInfo,
      successionRequiredVisibleInfo,
      hasCommercialPropertyRequiredVisibleInfo,
      revenueModelRequiredVisibleInfo,
    }),
    [
      amountRequiredVisibleInfo,
      amountFromRequiredVisibleInfo,
      amountToRequiredVisibleInfo,
      revenueRequiredVisibleInfo,
      COGSRequiredVisibleInfo,
      lessOverheadsRequiredVisibleInfo,
      depreciationAndAmortisationRequiredVisibleInfo,
      EBITDARequiredVisibleInfo,
      EBITRequiredVisibleInfo,
      accountingPeriodRequiredVisibleInfo,
      averageStockValueRequiredVisibleInfo,
      successionRequiredVisibleInfo,
      hasCommercialPropertyRequiredVisibleInfo,
      revenueModelRequiredVisibleInfo,
    ],
  );

  const [fields, setFields] =
    useState<Partial<ListingRequiredVisibleInfo>>(initialValue);

  const { popServerError } = useContext(PopNotificationsContext);

  const updateField = useCallback(
    async (
      fieldName: keyof ListingRequiredVisibleInfo,
      values: RequiredVisibleInfo,
      onSuccess: () => void,
    ) => {
      const data = prepareListingRequiredVisibleInfo(values);

      try {
        api.listings.patchListing(listingId, { [fieldName]: data });
        setFields((prev) => ({ ...prev, [fieldName]: data }));
        onSuccess();
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, popServerError],
  );

  return (
    <ListingFieldsRequiredVisibleInfoContext.Provider
      value={{ fields, canChangeRequired, updateField }}
    >
      {children}
    </ListingFieldsRequiredVisibleInfoContext.Provider>
  );
};

export default ListingFieldsRequiredVisibleInfo;
